import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { api, ApiError } from '../api/client.js'

interface BoardRow {
  booking_id: string
  booking_no: string
  origin: string
  destination: string
  customer_name: string
  trip_id: string | null
  vehicle_source: 'Own' | 'Outsourced' | null
  trip_status: string | null
  registration_no: string | null
  driver_name: string | null
  planning_stage: 'PendingPlanning' | 'OwnVehicleAssigned' | 'OutsourcedRequested' | 'FullyAssigned'
}

const STAGE_LABELS: Record<BoardRow['planning_stage'], string> = {
  PendingPlanning: 'Pending Planning',
  OwnVehicleAssigned: 'Own Vehicle Assigned',
  OutsourcedRequested: 'Outsourced Requested',
  FullyAssigned: 'Fully Assigned',
}

/**
 * Stage 4 §4.2: "a simpler flat list is the reliable baseline" — explicitly
 * sanctioned as an acceptable first version of the kanban board. Rows are
 * grouped by planning_stage (the same field the API computes) rather than
 * rendered as four separate droppable columns.
 */
export function VehiclePlanningPage() {
  const [rows, setRows] = useState<BoardRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [assignVehicleFor, setAssignVehicleFor] = useState<BoardRow | null>(null)
  const [assignDriverFor, setAssignDriverFor] = useState<BoardRow | null>(null)
  const [offersFor, setOffersFor] = useState<BoardRow | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.get<BoardRow[]>('/vehicle-planning')
      setRows(data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function requestOutsourced(row: BoardRow) {
    try {
      await api.post('/trips', { booking_id: row.booking_id, vehicle_source: 'Outsourced' })
      await load()
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : 'Failed to create trip')
    }
  }

  const order: BoardRow['planning_stage'][] = [
    'PendingPlanning',
    'OwnVehicleAssigned',
    'OutsourcedRequested',
    'FullyAssigned',
  ]
  const sorted = [...rows].sort((a, b) => order.indexOf(a.planning_stage) - order.indexOf(b.planning_stage))

  return (
    <div>
      <h2 style={{ margin: '0 0 0.75rem' }}>Vehicle Planning</h2>
      {error && <p className="tf-error">{error}</p>}

      <table className="tf-table">
        <thead>
          <tr>
            <th>Stage</th>
            <th>Booking</th>
            <th>Customer</th>
            <th>Route</th>
            <th>Vehicle / Driver</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={6}>Loading…</td>
            </tr>
          )}
          {!loading && sorted.length === 0 && (
            <tr>
              <td colSpan={6}>No bookings awaiting planning</td>
            </tr>
          )}
          {!loading &&
            sorted.map((row) => (
              <tr key={row.booking_id}>
                <td>{STAGE_LABELS[row.planning_stage]}</td>
                <td>{row.booking_no}</td>
                <td>{row.customer_name}</td>
                <td>
                  {row.origin} → {row.destination}
                </td>
                <td>
                  {row.registration_no ?? (row.vehicle_source === 'Outsourced' ? 'Outsourced' : '—')}
                  {row.driver_name ? ` · ${row.driver_name}` : ''}
                </td>
                <td className="tf-row-actions">
                  {row.planning_stage === 'PendingPlanning' && (
                    <>
                      <button type="button" onClick={() => setAssignVehicleFor(row)}>
                        Assign Own Vehicle
                      </button>
                      <button type="button" onClick={() => void requestOutsourced(row)}>
                        Request Outsourced
                      </button>
                    </>
                  )}
                  {row.planning_stage === 'OwnVehicleAssigned' && (
                    <button type="button" onClick={() => setAssignDriverFor(row)}>
                      Assign Driver
                    </button>
                  )}
                  {row.planning_stage === 'OutsourcedRequested' && (
                    <button type="button" onClick={() => setOffersFor(row)}>
                      Broker Offers
                    </button>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {assignVehicleFor && (
        <AssignOwnVehicleModal
          row={assignVehicleFor}
          onClose={() => setAssignVehicleFor(null)}
          onDone={() => {
            setAssignVehicleFor(null)
            void load()
          }}
        />
      )}
      {assignDriverFor && (
        <AssignDriverModal
          row={assignDriverFor}
          onClose={() => setAssignDriverFor(null)}
          onDone={() => {
            setAssignDriverFor(null)
            void load()
          }}
        />
      )}
      {offersFor && (
        <BrokerOffersModal
          row={offersFor}
          onClose={() => setOffersFor(null)}
          onDone={() => void load()}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
function AssignOwnVehicleModal({
  row,
  onClose,
  onDone,
}: {
  row: BoardRow
  onClose: () => void
  onDone: () => void
}) {
  const [vehicleTypes, setVehicleTypes] = useState<{ id: string; name: string }[]>([])
  const [vehicleTypeId, setVehicleTypeId] = useState('')
  const [vehicles, setVehicles] = useState<
    { id: string; registration_no: string; is_alternate: boolean; incident_count: number }[]
  >([])
  const [vehicleId, setVehicleId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    // /vehicle-types is a master endpoint — {rows, total, ...}, not a bare array.
    void api
      .get<{ rows: { id: string; name: string }[] }>('/vehicle-types?pageSize=100')
      .then((res) => setVehicleTypes(res.rows))
  }, [])

  useEffect(() => {
    if (!vehicleTypeId) {
      setVehicles([])
      return
    }
    void api
      .get<{ id: string; registration_no: string; is_alternate: boolean; incident_count: number }[]>(
        `/vehicles/available?vehicle_type_id=${vehicleTypeId}`,
      )
      .then(setVehicles)
  }, [vehicleTypeId])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await api.post('/trips', { booking_id: row.booking_id, vehicle_source: 'Own', vehicle_id: vehicleId })
      onDone()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to assign vehicle')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>Assign Own Vehicle — {row.booking_no}</h2>
        <form onSubmit={submit} className="tf-form">
          <label className="tf-field">
            <span>Vehicle type</span>
            <select value={vehicleTypeId} onChange={(e) => setVehicleTypeId(e.target.value)} required>
              <option value="" disabled>
                Select…
              </option>
              {vehicleTypes.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </label>
          <label className="tf-field">
            <span>Available vehicle</span>
            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} required disabled={!vehicleTypeId}>
              <option value="" disabled>
                Select…
              </option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.registration_no}
                  {v.is_alternate ? ' (alternate type)' : ''}
                  {/* Stage 7 §4.1/§4.3: soft warning, never blocks selection. */}
                  {v.incident_count > 0 ? ` ⚠ ${v.incident_count} prior incident${v.incident_count > 1 ? 's' : ''}` : ''}
                </option>
              ))}
            </select>
            {vehicleTypeId && vehicles.length === 0 && (
              <small>No Available vehicles of this type (or a compatible alternate) right now.</small>
            )}
          </label>
          {error && <p className="tf-error">{error}</p>}
          <div className="tf-form-actions">
            <button type="submit" className="tf-button-primary" disabled={saving || !vehicleId}>
              {saving ? 'Saving…' : 'Assign'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
function AssignDriverModal({ row, onClose, onDone }: { row: BoardRow; onClose: () => void; onDone: () => void }) {
  const [drivers, setDrivers] = useState<{ id: string; name: string; on_active_trip: boolean }[]>([])
  const [driverId, setDriverId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pendingOverride, setPendingOverride] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void api
      .get<{ id: string; name: string; on_active_trip: boolean }[]>('/drivers/active?exclude_expired_license=true')
      .then(setDrivers)
  }, [])

  async function submit(e: FormEvent, confirm = false) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await api.put(`/trips/${row.trip_id}/driver${confirm ? '?confirm=true' : ''}`, { driver_id: driverId })
      onDone()
    } catch (err) {
      if (err instanceof ApiError && err.requiresOverride) {
        setPendingOverride(err.message)
      } else {
        setError(err instanceof ApiError ? err.message : 'Failed to assign driver')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>Assign Driver — {row.booking_no}</h2>
        <form onSubmit={(e) => void submit(e)} className="tf-form">
          <label className="tf-field">
            <span>Driver</span>
            <select value={driverId} onChange={(e) => setDriverId(e.target.value)} required>
              <option value="" disabled>
                Select…
              </option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.on_active_trip ? ' (already on another active trip)' : ''}
                </option>
              ))}
            </select>
          </label>
          {error && <p className="tf-error">{error}</p>}
          {pendingOverride && (
            <div className="tf-override-banner">
              <p>{pendingOverride}</p>
              <button type="button" className="tf-button-primary" disabled={saving} onClick={(e) => void submit(e, true)}>
                {saving ? 'Saving…' : 'Override & Confirm'}
              </button>
            </div>
          )}
          <div className="tf-form-actions">
            <button type="submit" className="tf-button-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Assign'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
interface Offer {
  id: string
  broker_name: string
  offered_vehicle_no: string
  offered_driver_name: string | null
  hire_cost: string
  brokerage_type: string
  brokerage_value: string
  approval_status: string
}

function BrokerOffersModal({ row, onClose, onDone }: { row: BoardRow; onClose: () => void; onDone: () => void }) {
  const [offers, setOffers] = useState<Offer[]>([])
  const [brokers, setBrokers] = useState<{ id: string; name: string }[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // New-offer form state
  const [brokerId, setBrokerId] = useState('')
  const [vehicleNo, setVehicleNo] = useState('')
  const [driverName, setDriverName] = useState('')
  const [driverPhone, setDriverPhone] = useState('')
  const [hireCost, setHireCost] = useState('')
  const [brokerageType, setBrokerageType] = useState<'Flat' | 'Percent'>('Flat')
  const [brokerageValue, setBrokerageValue] = useState('')
  const [pendingOverride, setPendingOverride] = useState<string | null>(null)
  const [vehicleIncidentCount, setVehicleIncidentCount] = useState<number | null>(null)

  const reload = useCallback(async () => {
    const [offersRes, brokersRes] = await Promise.all([
      api.get<Offer[]>(`/trips/${row.trip_id}/outsourced-offers`),
      api.get<{ rows: { id: string; name: string }[] }>('/brokers?pageSize=100'),
    ])
    setOffers(offersRes)
    setBrokers(brokersRes.rows)
  }, [row.trip_id])

  useEffect(() => {
    void reload()
  }, [reload])

  async function logOffer(e: FormEvent, confirm = false) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post(`/trips/${row.trip_id}/outsourced-offers${confirm ? '?confirm=true' : ''}`, {
        broker_id: brokerId,
        offered_vehicle_no: vehicleNo,
        offered_driver_name: driverName || undefined,
        offered_driver_phone: driverPhone || undefined,
        hire_cost: hireCost,
        brokerage_type: brokerageType,
        brokerage_value: brokerageValue,
      })
      setVehicleNo('')
      setDriverName('')
      setDriverPhone('')
      setHireCost('')
      setBrokerageValue('')
      setPendingOverride(null)
      await reload()
    } catch (err) {
      if (err instanceof ApiError && err.requiresOverride) {
        setPendingOverride(err.message)
      } else {
        setError(err instanceof ApiError ? err.message : 'Failed to log offer')
      }
    } finally {
      setBusy(false)
    }
  }

  async function approve(offerId: string) {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/outsourced-offers/${offerId}/approve`)
      await reload()
      onDone()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Approval failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="tf-slideover-backdrop" onClick={onClose}>
      <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
        <h2>Broker Offers — {row.booking_no}</h2>

        {offers.map((o) => (
          <div key={o.id} className="tf-card" style={{ marginTop: '0.5rem' }}>
            <p className="tf-note">
              <strong>{o.broker_name}</strong> — {o.offered_vehicle_no} ({o.offered_driver_name ?? 'no driver named'})
              <br />
              Hire ₹{o.hire_cost} · Brokerage {o.brokerage_type === 'Percent' ? `${o.brokerage_value}%` : `₹${o.brokerage_value}`}
              <br />
              Status: {o.approval_status}
            </p>
            {o.approval_status === 'Pending' && (
              <button type="button" disabled={busy} onClick={() => void approve(o.id)}>
                Approve
              </button>
            )}
          </div>
        ))}

        <form onSubmit={(e) => void logOffer(e)} className="tf-form" style={{ marginTop: '1rem' }}>
          <h2>Log a new offer</h2>
          <label className="tf-field">
            <span>Broker</span>
            <select value={brokerId} onChange={(e) => setBrokerId(e.target.value)} required>
              <option value="" disabled>
                Select…
              </option>
              {brokers.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          <label className="tf-field">
            <span>Offered vehicle number</span>
            <input
              value={vehicleNo}
              onChange={(e) => {
                setVehicleNo(e.target.value)
                setVehicleIncidentCount(null)
              }}
              onBlur={async () => {
                // Stage 7 §4.1: "the moment a broker's offered vehicle number
                // is typed in" — market vehicles are exactly where there's
                // less control and more need for this warning.
                if (!vehicleNo.trim()) return
                try {
                  const res = await api.get<{ count: number }>(
                    `/vehicle-incidents?registration_no=${encodeURIComponent(vehicleNo)}`,
                  )
                  setVehicleIncidentCount(res.count)
                } catch {
                  setVehicleIncidentCount(null)
                }
              }}
              required
            />
            {vehicleIncidentCount !== null && vehicleIncidentCount > 0 && (
              <small style={{ color: 'var(--tf-warn, #b45309)' }}>
                ⚠ {vehicleIncidentCount} prior incident{vehicleIncidentCount > 1 ? 's' : ''} logged against this vehicle
              </small>
            )}
          </label>
          <label className="tf-field">
            <span>Driver name</span>
            <input value={driverName} onChange={(e) => setDriverName(e.target.value)} />
          </label>
          <label className="tf-field">
            <span>Driver mobile</span>
            <input value={driverPhone} onChange={(e) => setDriverPhone(e.target.value)} />
          </label>
          <label className="tf-field">
            <span>Hire cost (₹)</span>
            <input type="number" value={hireCost} onChange={(e) => setHireCost(e.target.value)} required />
          </label>
          <label className="tf-field">
            <span>Brokerage type</span>
            <select value={brokerageType} onChange={(e) => setBrokerageType(e.target.value as 'Flat' | 'Percent')}>
              <option value="Flat">Flat amount</option>
              <option value="Percent">Percentage</option>
            </select>
          </label>
          <label className="tf-field">
            <span>Brokerage value</span>
            <input type="number" value={brokerageValue} onChange={(e) => setBrokerageValue(e.target.value)} required />
          </label>

          {error && <p className="tf-error">{error}</p>}
          {pendingOverride && (
            <div className="tf-override-banner">
              <p>{pendingOverride}</p>
              <button type="button" className="tf-button-primary" disabled={busy} onClick={(e) => void logOffer(e, true)}>
                {busy ? 'Saving…' : 'Override & Confirm'}
              </button>
            </div>
          )}
          <button type="submit" className="tf-button-primary" disabled={busy}>
            Log Offer
          </button>
        </form>

        <div className="tf-form-actions" style={{ marginTop: '1rem' }}>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
